import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomIconFont } from 'icons';
import { debounce, DebouncedFunc } from 'lodash-es';

import { classnames } from 'lib/classnames';
import { DEFAULT_WORKSPACE_ICON } from 'lib/constants/icon';
import { polyfillIcon } from 'lib/icons/utils';
import { Workspace } from 'lib/models';
import { escapeMatchesQueryArg } from 'lib/parse';

import styles from './index.module.less';

const cx = classnames(styles);

interface Option {
  label: React.ReactNode;
  value: string;
}

const workspacesToOptions = (workspaces: Parse.Attributes[]): Option[] => {
  return workspaces.map(workspace => {
    const [, icon] = polyfillIcon(workspace.icon, DEFAULT_WORKSPACE_ICON);
    workspace.icon = icon;
    return {
      value: workspace.objectId,
      label: (
        <>
          <CustomIconFont type={workspace.icon} className={cx('workspace-icon')} />
          {workspace.name}
        </>
      ),
    };
  });
};

const PAGE_LIMIT = 100;

const useWorkspaceSelect = (
  value: string,
  change: (workspace: Parse.Attributes) => void,
): {
  data: Parse.Attributes;
  options: Option[];
  loading: boolean;
  debounceFetcher: DebouncedFunc<(keyword: string) => Promise<void>>;
  getWorkspaceDetail: (id: string) => Promise<Parse.Attributes>;
  fetchMore: () => void;
} => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(undefined);
  const [options, setOptions] = useState<Option[]>([]);
  const isLoadMore = useRef(false);
  const page = useRef(1);
  const isSearch = useRef(false);
  // 缓存已经加载过的数据
  const cacheList = useRef<Option[]>([]);
  // 搜索相关的数据
  const searchCacheList = useRef<Option[]>([]);
  const searchPage = useRef(1);
  const cacheKeyWord = useRef('');

  const fetchWorkspaceDetail = useCallback(async (workspaceId: string): Promise<Parse.Attributes> => {
    setLoading(true);
    try {
      const workspace = await new Parse.Query(Workspace)
        .equalTo('objectId', workspaceId)
        .include('itemTypeScheme,itemTypeScreenScheme,workflowScheme')
        .select([
          'name',
          'key',
          'icon',
          'itemTypeScheme.hierarchy',
          'itemTypeScreenScheme',
          'workflowScheme',
          'securityLevelScheme',
        ])
        .first({ json: true });
      return workspace;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkspacesByPage = useCallback(async () => {
    const workspaceQuery = new Parse.Query(Workspace);
    if (value) {
      workspaceQuery.notEqualTo('objectId', value);
    }
    const result = await workspaceQuery
      .addDescending('createdAt')
      .select([
        'name',
        'key',
        'icon',
        'itemTypeScheme',
        'itemTypeScreenScheme',
        'workflowScheme',
        'securityLevelScheme',
      ])
      .skip((page.current - 1) * PAGE_LIMIT)
      .limit(PAGE_LIMIT)
      .find({ json: true });
    if (result.length > 0) {
      page.current += 1;
      cacheList.current.push(...workspacesToOptions(result));
    }
    setOptions([...cacheList.current]);
  }, [value]);

  const searchWorkspaceByPage = useCallback(async () => {
    const workspaceQuery = new Parse.Query(Workspace);
    if (cacheKeyWord.current) {
      workspaceQuery.matches('name', escapeMatchesQueryArg(cacheKeyWord.current), 'i');
    }
    workspaceQuery.skip((searchPage.current - 1) * PAGE_LIMIT).limit(PAGE_LIMIT);
    const result = await workspaceQuery.find({ json: true });
    if (result.length > 0) {
      searchPage.current += 1;
      searchCacheList.current.push(...workspacesToOptions(result));
    }
    setOptions([...searchCacheList.current]);
  }, []);

  const fetchMore = useCallback(async () => {
    if (isLoadMore.current) return;
    isLoadMore.current = true;
    try {
      if (isSearch.current) {
        await searchWorkspaceByPage();
      } else {
        await fetchWorkspacesByPage();
      }
    } finally {
      isLoadMore.current = false;
    }
  }, [searchWorkspaceByPage, fetchWorkspacesByPage]);

  useEffect(() => {
    const fetchInit = async () => {
      let workspace = null;
      if (value) {
        workspace = await fetchWorkspaceDetail(value);
        if (workspace) {
          cacheList.current.push(...workspacesToOptions([workspace]));
          setData(workspace);
          change?.(workspace);
        }
      }
      fetchWorkspacesByPage();
    };
    fetchInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const debounceFetcher = useMemo(() => {
    const loadOptions = async (keyword: string) => {
      isSearch.current = !!keyword;
      if (!keyword) {
        // 没有关键字时，展示缓存数据
        setOptions([...cacheList.current]);
        return;
      }
      setLoading(true);
      cacheKeyWord.current = keyword?.trim?.();
      searchPage.current = 1;
      searchCacheList.current = [];
      try {
        await searchWorkspaceByPage();
      } finally {
        setLoading(false);
      }
    };

    return debounce(loadOptions, 450);
  }, [searchWorkspaceByPage]);

  const getWorkspaceDetail = useCallback(async (workspaceId: string): Promise<Parse.Attributes> => {
    const workspaceDetail = await fetchWorkspaceDetail(workspaceId);
    setData(workspaceDetail);
    return workspaceDetail;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, options, loading, debounceFetcher, getWorkspaceDetail, fetchMore };
};

export { useWorkspaceSelect };
